import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Copy, Pause, Play, Archive, Pencil, ExternalLink } from "lucide-react";
import ProviderDashboardLayout from "@/layouts/ProviderDashboardLayout";
import StatusPill from "@/components/marketplace/StatusPill";
import api from "@/lib/api";

export default function ServicesListPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/services/mine/list");
      setServices(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = services.filter((s) => (filter === "all" ? true : s.status === filter));

  const action = async (label, endpoint) => {
    try {
      await api.post(endpoint);
      toast.success(label);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Action failed");
    }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/services/${id}/duplicate`);
      toast.success("Service duplicated");
      navigate(`/dashboard/services/${data.service_id}/edit`);
    } catch (err) {
      toast.error("Could not duplicate");
    }
  };

  return (
    <ProviderDashboardLayout>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">My services</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">Service listings</h1>
          <p className="text-[#252A34]/70 mt-1 text-sm">Create, edit, pause, and duplicate your marketplace listings.</p>
        </div>
        <div className="flex gap-2">
        <Link to="/dashboard/services/import" data-testid="services-list-import" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 font-medium hover:border-[#165a71]/40">Import services</Link>
        <Link to="/dashboard/services/new" data-testid="services-list-new" className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2.5 font-medium hover:bg-[#11485a] transition-colors">
          <Plus className="h-4 w-4" /> New service
        </Link>
        </div>
      </div>

      <div className="mb-4 inline-flex items-center gap-1 rounded-full bg-slate-100 p-1" data-testid="services-list-filters">
        {[
          { k: "all", l: `All (${services.length})` },
          { k: "published", l: "Published" },
          { k: "draft", l: "Drafts" },
          { k: "pending_review", l: "Pending" },
          { k: "paused", l: "Paused" },
          { k: "archived", l: "Archived" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`services-filter-${f.k}`}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === f.k ? "bg-white text-[#071b22] shadow-sm" : "text-[#252A34]/70"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#252A34]/60" data-testid="services-list-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="services-list-empty">
          <div className="text-[#252A34]/70">No services in this bucket.</div>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((s) => (
            <div key={s.id} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-4" data-testid={`svc-row-${s.id}`}>
              <img src={s.featured_image || "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=400"} alt="" className="h-16 w-16 rounded-xl object-cover" />
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold text-[#071b22] truncate flex items-center gap-2">
                  {s.title}
                  {s.is_featured && <span className="text-amber-600 text-[11px] font-medium">FEATURED</span>}
                </div>
                <div className="text-xs text-[#252A34]/60 truncate mt-0.5">{s.short_description || s.pricing_model}</div>
                <div className="mt-1.5 text-xs text-[#252A34]/60">from ${s.base_price} · {s.service_type?.replace(/_/g, " ")}</div>
              </div>
              <StatusPill status={s.status} />
              <div className="flex items-center gap-1.5">
                {s.status === "published" && (
                  <a href={`/services/${s.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg border border-slate-200 hover:border-[#165a71]/40 text-[#252A34]" data-testid={`view-service-${s.id}`}>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                <Link to={`/dashboard/services/${s.id}/edit`} className="p-2 rounded-lg border border-slate-200 hover:border-[#165a71]/40 text-[#252A34]" data-testid={`edit-btn-${s.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Link>
                <button onClick={() => duplicate(s.id)} className="p-2 rounded-lg border border-slate-200 hover:border-[#165a71]/40 text-[#252A34]" data-testid={`duplicate-btn-${s.id}`}>
                  <Copy className="h-3.5 w-3.5" />
                </button>
                {s.status === "published" ? (
                  <button onClick={() => action("Paused", `/services/${s.id}/pause`)} className="p-2 rounded-lg border border-slate-200 hover:border-amber-300 text-[#252A34]" data-testid={`pause-btn-${s.id}`}>
                    <Pause className="h-3.5 w-3.5" />
                  </button>
                ) : s.status === "paused" ? (
                  <button onClick={() => action("Resumed", `/services/${s.id}/resume`)} className="p-2 rounded-lg border border-slate-200 hover:border-emerald-300 text-[#252A34]" data-testid={`resume-btn-${s.id}`}>
                    <Play className="h-3.5 w-3.5" />
                  </button>
                ) : null}
                <button onClick={() => action("Archived", `/services/${s.id}/archive`)} className="p-2 rounded-lg border border-slate-200 hover:border-red-300 text-[#252A34]" data-testid={`archive-btn-${s.id}`}>
                  <Archive className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </ProviderDashboardLayout>
  );
}
